/**
 * 商談詳細「請求・発注・発送」タブ。
 *
 * 3カラム構成 (左: 請求 / 中央: 発注 / 右: 発送)
 * 各カラムでステータスを直接操作できる。
 */
import type {
  BillingAddress,
  DeliveryStatus,
  InvoiceStatus,
  PurchaseOrderStatus,
} from '@goenhub/shared';
import {
  countDeliverySerials,
  getBillingAddresses,
  getDefaultBillingAddress,
  PURCHASE_ORDER_STATUS_LABEL,
} from '@goenhub/shared';
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Mail,
  Pencil,
  Receipt,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import { Calendar } from 'lucide-react';
import { CheckCircle, Clock } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { DocumentMailDialog } from '../components/DocumentMailDialog';
import { PurchaseOrderFulfillmentPanel } from '../components/PurchaseOrderFulfillmentPanel';
import { TimeWaverPurchaseOrderBlock } from '../components/TimeWaverPurchaseOrderBlock';
import { SerialNumbersEditDialog } from '../dialogs/SerialNumbersEditDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import {
  useConsentSubmissionsByDeal,
  useDealConsentRequirement,
} from '@/hooks/useConsentDocuments';
import { type DealRecord } from '@/hooks/useDeals';
import { useMailContact } from '@/hooks/useMailContacts';
import { useProductCategories } from '@/hooks/useProductCategories';
import {
  useDealDeliveries,
  useDealInvoices,
  useDealPurchaseOrders,
  useUpdateDelivery,
  useUpdateInvoiceDates,
  useUpdateInvoiceMeta,
  useUpdateInvoiceStatus,
  useUpdatePurchaseOrder,
} from '@/hooks/useSalesOps';
import {
  canEditDocumentDates,
  DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE,
} from '@/lib/documentDatePermission';
import { formatDate, toDate } from '@/lib/firestoreTime';
import { PO_STATUS_VARIANT } from '@/lib/purchaseOrderStatus';
import { paths } from '@/routes/paths';

const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  unpaid: '未入金',
  paid: '入金済',
  overdue: '期限超過',
  cancelled: 'キャンセル',
};

const INVOICE_VARIANT: Record<InvoiceStatus, 'default' | 'draft' | 'approved' | 'danger'> = {
  unpaid: 'draft',
  paid: 'approved',
  overdue: 'danger',
  cancelled: 'default',
};

const DELIVERY_LABEL: Record<DeliveryStatus, string> = {
  pending: '準備中',
  in_transit: '配送中',
  completed: '完了',
  cancelled: 'キャンセル',
};

const DELIVERY_VARIANT: Record<DeliveryStatus, 'default' | 'draft' | 'sent' | 'approved'> = {
  pending: 'draft',
  in_transit: 'sent',
  completed: 'approved',
  cancelled: 'default',
};

interface Props {
  deal: DealRecord;
  /** TimeWaver の発注カードのうち、スクロールして強調する発注 (発注一覧・次のアクション等から開いた時)。 */
  focusPoId?: string | null;
  focusNonce?: number;
}

export function DealFulfillmentTab({ deal, focusPoId, focusNonce }: Props) {
  const invoices = useDealInvoices(deal.id);
  const purchaseOrders = useDealPurchaseOrders(deal.id);
  const deliveries = useDealDeliveries(deal.id);
  const { data: contact } = useMailContact(deal.contactId);
  const { data: categories } = useProductCategories({ includeInactive: true });

  // v3.1.8: 主軸カテゴリ = TimeWaver なら専用 PO ブロックに差し替え
  const isTimeWaver =
    !!deal.primaryCategoryId &&
    (categories ?? []).some((c) => c.id === deal.primaryCategoryId && c.name === 'TimeWaver');

  // v3.2.25: 主軸カテゴリの requiresConsent
  // 2026-10-16: 同じ主軸カテゴリーの別の商談でサイン済みの顧客は省略 (waivedBy にそのサイン)
  const { requiresConsent, waivedBy: consentWaivedBy } = useDealConsentRequirement(deal);
  const consentSubmissions = useConsentSubmissionsByDeal(requiresConsent ? deal.id : null);
  const consentSigned = (consentSubmissions.data?.length ?? 0) > 0;

  const updateInvoice = useUpdateInvoiceStatus();
  const updateInvoiceDates = useUpdateInvoiceDates();
  const { user } = useAuth();
  const updateInvoiceMeta = useUpdateInvoiceMeta();
  const updatePO = useUpdatePurchaseOrder();
  const updateDelivery = useUpdateDelivery();
  const [serialEditDeliveryId, setSerialEditDeliveryId] = useState<string | null>(null);
  const [editingInvoiceDates, setEditingInvoiceDates] = useState<{
    invoiceId: string;
    issuedAt: string;
    dueDate: string;
  } | null>(null);
  // 件名 / 請求先住所の編集。billingChoice: 登録住所id | '__DEFAULT__'(顧客の既定) | '__CURRENT__'(現行スナップショット維持)
  const [editingInvoiceMeta, setEditingInvoiceMeta] = useState<{
    invoiceId: string;
    title: string;
    billingChoice: string;
  } | null>(null);
  const [mailInvoiceId, setMailInvoiceId] = useState<string | null>(null);
  const editingDelivery = deliveries.data?.find((d) => d.id === serialEditDeliveryId) ?? null;

  const handleMarkPaid = async (invoiceId: string) => {
    try {
      await updateInvoice.mutateAsync({
        dealId: deal.id,
        invoiceId,
        status: 'paid',
        paymentMethod: 'transfer',
      });
      toast.success('入金確認しました。発注書を自動起票します。');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const handleAdvancePO = async (poId: string, nextStatus: PurchaseOrderStatus) => {
    try {
      await updatePO.mutateAsync({
        dealId: deal.id,
        poId,
        status: nextStatus,
      });
      toast.success(nextStatus === 'ordered' ? '発注しました' : '更新しました');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const handleAdvanceDelivery = async (deliveryId: string, nextStatus: DeliveryStatus) => {
    try {
      await updateDelivery.mutateAsync({
        dealId: deal.id,
        deliveryId,
        status: nextStatus,
      });
      toast.success(
        nextStatus === 'completed'
          ? '納品完了しました。稼働機器を自動登録します。'
          : '更新しました',
      );
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    // TimeWaver は発注欄に書類 3 種が並ぶため、列幅を 請求 1：発注 2：発送 1 にする
    <div
      className={`grid grid-cols-1 gap-4 items-start ${isTimeWaver ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
    >
      {/* 請求 */}
      <Section title="請求" icon={Receipt}>
        {invoices.isLoading ? (
          <Spinner />
        ) : !invoices.data || invoices.data.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="請求書なし"
            description="成約確定 (markDealWon) で自動起票されます。"
          />
        ) : (
          <div className="space-y-2">
            {invoices.data.map((iv) => (
              <Card key={iv.id}>
                <RowHeader>
                  <span className="font-mono text-xs">{iv.invoiceNumber}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const snap = iv.billingAddressSnapshot ?? null;
                        const registered = contact ? getBillingAddresses(contact) : [];
                        const billingChoice = snap
                          ? registered.some((a) => a.id === snap.id)
                            ? snap.id
                            : '__CURRENT__'
                          : '__DEFAULT__';
                        setEditingInvoiceMeta({
                          invoiceId: iv.id,
                          title: iv.title ?? '',
                          billingChoice,
                        });
                      }}
                      className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]"
                      title="件名 / 請求先住所を編集"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                    <Badge variant={INVOICE_VARIANT[iv.status]}>{INVOICE_LABEL[iv.status]}</Badge>
                  </div>
                </RowHeader>
                <KV label="金額">¥{iv.amount.toLocaleString('ja-JP')}</KV>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <KV label="発行日">{fmtDate(iv.issuedAt)}</KV>
                    <KV label="期限">{fmtDate(iv.dueDate)}</KV>
                  </div>
                  {canEditDocumentDates(user, deal, iv, 'invoice') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      title="発行日 / 支払期限を編集"
                      onClick={() => {
                        const issued = toDate(iv.issuedAt);
                        const due = toDate(iv.dueDate);
                        const fmt = (d: Date | null) =>
                          d
                            ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                            : '';
                        setEditingInvoiceDates({
                          invoiceId: iv.id,
                          issuedAt: fmt(issued),
                          dueDate: fmt(due),
                        });
                      }}
                    >
                      <Calendar className="size-3.5" />
                    </Button>
                  )}
                </div>
                {iv.paidAt && <KV label="入金日">{fmtDate(iv.paidAt)}</KV>}
                {iv.publicToken && (
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        const url = `${window.location.origin}${paths.publicInvoice.replace(':token', iv.publicToken!)}`;
                        navigator.clipboard.writeText(url);
                        toast.success('公開URLをコピーしました');
                      }}
                      title="公開URLをコピー"
                    >
                      <Copy className="size-3.5" />
                      URL
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        const url = `${window.location.origin}${paths.publicInvoice.replace(':token', iv.publicToken!)}`;
                        window.open(url, '_blank');
                      }}
                      title="印刷ページを開く"
                    >
                      <ExternalLink className="size-3.5" />
                      印刷
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => setMailInvoiceId(iv.id)}
                      title="請求書をメール送信"
                    >
                      <Mail className="size-3.5" />
                      メール
                    </Button>
                  </div>
                )}
                {iv.status === 'unpaid' && (
                  <>
                    {requiresConsent && (
                      <div
                        className={`w-full mt-2 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded ${
                          consentSigned
                            ? 'bg-[var(--accent-green-subtle)] text-[var(--accent-green)]'
                            : 'bg-[var(--bg-hover)] text-[var(--text-muted)]'
                        }`}
                        title={
                          consentSigned ? '同意書サイン提出済み' : '同意書未提出 (顧客サイン待ち)'
                        }
                      >
                        {consentSigned ? (
                          <CheckCircle className="size-3.5" />
                        ) : (
                          <Clock className="size-3.5" />
                        )}
                        同意確認 {consentSigned ? '完了' : '未提出'}
                      </div>
                    )}
                    {consentWaivedBy && (
                      <div
                        className="w-full mt-2 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded bg-[var(--accent-green-subtle)] text-[var(--accent-green)]"
                        title={`${formatDate(consentWaivedBy.submittedAt)} に同じ主軸カテゴリーの別の商談で同意書にサイン済みのため、この商談では同意書は不要です`}
                      >
                        <CheckCircle className="size-3.5" />
                        同意確認 不要 (過去の商談でサイン済み)
                      </div>
                    )}
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleMarkPaid(iv.id)}
                      disabled={updateInvoice.isPending}
                      className="w-full mt-2"
                    >
                      <CheckCircle2 className="size-3.5" />
                      入金確認
                    </Button>
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </Section>

      {/* 発注 */}
      <Section
        title="発注 (仕入)"
        icon={ShoppingCart}
        className={isTimeWaver ? 'lg:col-span-2' : undefined}
      >
        {isTimeWaver ? (
          // TimeWaver 専用 UI (v3.1.8): メーカー見積アップロード/AI抽出/発注確定/送金申請
          purchaseOrders.isLoading ? (
            <Spinner />
          ) : !purchaseOrders.data || purchaseOrders.data.length === 0 ? (
            <EmptyState
              icon={ShoppingCart}
              title="発注書なし"
              description="入金確認 (paid) で自動起票されます。"
            />
          ) : (
            <TimeWaverPurchaseOrderBlock
              dealId={deal.id}
              purchaseOrders={purchaseOrders.data}
              contactName={contact?.name ?? null}
              manufacturerEmail={
                (categories ?? []).find((c) => c.id === deal.primaryCategoryId)
                  ?.manufacturerEmail ?? null
              }
              manufacturerCurrency={
                (categories ?? []).find((c) => c.id === deal.primaryCategoryId)
                  ?.manufacturerCurrency ?? 'EUR'
              }
              focusPoId={focusPoId ?? null}
              focusNonce={focusNonce}
            />
          )
        ) : purchaseOrders.isLoading ? (
          <Spinner />
        ) : !purchaseOrders.data || purchaseOrders.data.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="発注書なし"
            description="入金確認 (paid) で自動起票されます。"
          />
        ) : (
          <div className="space-y-2">
            {purchaseOrders.data.map((po) => (
              <Card key={po.id}>
                <RowHeader>
                  <span className="font-mono text-xs">{po.poNumber}</span>
                  <Badge variant={PO_STATUS_VARIANT[po.status]}>
                    {PURCHASE_ORDER_STATUS_LABEL[po.status]}
                  </Badge>
                </RowHeader>
                <KV label="仕入合計">¥{po.totalCostPrice.toLocaleString('ja-JP')}</KV>
                <KV label="行数">{po.lineItems.length} 行</KV>
                {po.supplierName && <KV label="仕入先">{po.supplierName}</KV>}
                {po.orderedAt && <KV label="発注日">{fmtDate(po.orderedAt)}</KV>}
                {po.status === 'pending' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAdvancePO(po.id, 'ordered')}
                    disabled={updatePO.isPending}
                    className="w-full mt-2"
                  >
                    <CheckCircle2 className="size-3.5" />
                    発注済みにする
                  </Button>
                )}
                {/* 2026-10-30〜: 入荷 → 検品・シリアル → 顧客へ発送 */}
                <div className="pt-2">
                  <PurchaseOrderFulfillmentPanel
                    dealId={deal.id}
                    po={po}
                    isTimeWaver={false}
                    contactName={contact?.name ?? null}
                  />
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>

      {/* 発送 */}
      <Section title="発送 / 納品" icon={Truck}>
        {deliveries.isLoading ? (
          <Spinner />
        ) : !deliveries.data || deliveries.data.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="発送なし"
            description="入荷・検品とシリアル入力が終わると、発注カードの「顧客へ発送」からここに作られます。"
          />
        ) : (
          <div className="space-y-2">
            {deliveries.data.map((dl) => {
              // 台数基準で数える (シリアル欄が空配列の発送でも台数分を未入力として扱う)
              const {
                total: totalSerials,
                filled: filledSerials,
                missing: missingSerials,
              } = countDeliverySerials(dl.lineItems);
              const allSerialsFilled = missingSerials === 0;
              const next: DeliveryStatus | null =
                dl.status === 'pending'
                  ? 'in_transit'
                  : dl.status === 'in_transit'
                    ? 'completed'
                    : null;
              return (
                <Card key={dl.id}>
                  <RowHeader>
                    <span className="font-mono text-xs">{dl.deliveryNumber}</span>
                    <Badge variant={DELIVERY_VARIANT[dl.status]}>{DELIVERY_LABEL[dl.status]}</Badge>
                  </RowHeader>
                  <KV label="行数">{dl.lineItems.length} 行</KV>
                  <KV label="シリアル">
                    <span
                      className={
                        allSerialsFilled
                          ? 'text-[var(--accent-green)]'
                          : 'text-[var(--accent-amber)]'
                      }
                    >
                      {filledSerials}/{totalSerials}
                    </span>
                  </KV>
                  {dl.carrierName && <KV label="配送業者">{dl.carrierName}</KV>}
                  {dl.trackingNumber && (
                    <KV label="追跡番号">
                      <span className="font-mono text-xs">{dl.trackingNumber}</span>
                    </KV>
                  )}
                  {dl.deliveredAt && <KV label="納品日">{fmtDate(dl.deliveredAt)}</KV>}
                  {/* シリアル入力ボタン (未完了かつ未入力あり) */}
                  {dl.status !== 'completed' && dl.status !== 'cancelled' && !allSerialsFilled && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSerialEditDeliveryId(dl.id)}
                      className="w-full mt-2"
                    >
                      <Pencil className="size-3.5" />
                      シリアル入力 ({missingSerials} 件未入力)
                    </Button>
                  )}
                  {next && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAdvanceDelivery(dl.id, next)}
                      disabled={
                        updateDelivery.isPending || (next === 'completed' && !allSerialsFilled)
                      }
                      className="w-full mt-2"
                    >
                      <CheckCircle2 className="size-3.5" />
                      {next === 'in_transit' ? '配送中にする' : '納品完了にする'}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </Section>

      <DocumentMailDialog
        open={mailInvoiceId !== null}
        onOpenChange={(o) => !o && setMailInvoiceId(null)}
        documentType="invoice"
        dealId={deal.id}
        documentId={mailInvoiceId}
      />

      {editingDelivery && (
        <SerialNumbersEditDialog
          open={serialEditDeliveryId !== null}
          dealId={deal.id}
          deliveryId={editingDelivery.id}
          deliveryNumber={editingDelivery.deliveryNumber}
          initialLineItems={editingDelivery.lineItems}
          onOpenChange={(o) => !o && setSerialEditDeliveryId(null)}
        />
      )}

      <Dialog
        open={editingInvoiceDates !== null}
        onOpenChange={(o) => !o && setEditingInvoiceDates(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>請求書の日付を編集</DialogTitle>
          </DialogHeader>
          {editingInvoiceDates && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">発行日</Label>
                <Input
                  type="date"
                  value={editingInvoiceDates.issuedAt}
                  onChange={(e) =>
                    setEditingInvoiceDates({
                      ...editingInvoiceDates,
                      issuedAt: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-xs">支払期限</Label>
                <Input
                  type="date"
                  value={editingInvoiceDates.dueDate}
                  onChange={(e) =>
                    setEditingInvoiceDates({
                      ...editingInvoiceDates,
                      dueDate: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditingInvoiceDates(null)}
              disabled={updateInvoiceDates.isPending}
            >
              キャンセル
            </Button>
            <Button
              variant="primary"
              disabled={updateInvoiceDates.isPending}
              onClick={async () => {
                if (!editingInvoiceDates) return;
                try {
                  await updateInvoiceDates.mutateAsync({
                    dealId: deal.id,
                    invoiceId: editingInvoiceDates.invoiceId,
                    issuedAt: editingInvoiceDates.issuedAt
                      ? new Date(editingInvoiceDates.issuedAt)
                      : null,
                    dueDate: editingInvoiceDates.dueDate
                      ? new Date(editingInvoiceDates.dueDate)
                      : null,
                  });
                  toast.success('日付を更新しました');
                  setEditingInvoiceDates(null);
                } catch (err) {
                  const code = (err as { code?: string }).code ?? '';
                  toast.error(
                    code === 'permission-denied'
                      ? DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE
                      : `日付の更新に失敗しました: ${(err as Error).message}`,
                  );
                }
              }}
            >
              {updateInvoiceDates.isPending ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 件名 / 請求先住所の編集 */}
      <Dialog
        open={editingInvoiceMeta !== null}
        onOpenChange={(o) => !o && setEditingInvoiceMeta(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>請求書を編集</DialogTitle>
          </DialogHeader>
          {editingInvoiceMeta &&
            (() => {
              const metaInvoice =
                invoices.data?.find((i) => i.id === editingInvoiceMeta.invoiceId) ?? null;
              const registeredBilling = contact ? getBillingAddresses(contact) : [];
              const currentSnap = metaInvoice?.billingAddressSnapshot ?? null;
              const currentIsUnregistered =
                !!currentSnap && !registeredBilling.some((a) => a.id === currentSnap.id);
              const fmtAddr = (a: BillingAddress) =>
                [
                  a.label ? `[${a.label}]` : '',
                  a.recipientName ?? '',
                  a.postalCode ? `〒${a.postalCode}` : '',
                  a.prefecture ?? '',
                  a.address ?? '',
                  a.buildingName ?? '',
                ]
                  .filter(Boolean)
                  .join(' ') || '(住所未設定)';
              return (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">件名</Label>
                    <Input
                      value={editingInvoiceMeta.title}
                      onChange={(e) =>
                        setEditingInvoiceMeta({
                          ...editingInvoiceMeta,
                          title: e.target.value,
                        })
                      }
                      placeholder="ご請求書の件名"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">請求先住所</Label>
                    {registeredBilling.length === 0 && !currentSnap ? (
                      <p className="text-[11px] text-[var(--text-muted)] italic mt-1">
                        顧客に請求先住所が登録されていません。顧客詳細画面で追加してください。
                      </p>
                    ) : (
                      <select
                        value={editingInvoiceMeta.billingChoice}
                        onChange={(e) =>
                          setEditingInvoiceMeta({
                            ...editingInvoiceMeta,
                            billingChoice: e.target.value,
                          })
                        }
                        className="w-full px-2 py-1.5 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-sm"
                      >
                        <option value="__DEFAULT__">顧客の既定を使用</option>
                        {currentIsUnregistered && (
                          <option value="__CURRENT__">
                            現在の住所を維持（{fmtAddr(currentSnap!)}）
                          </option>
                        )}
                        {registeredBilling.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.isDefault ? '★ ' : ''}
                            {fmtAddr(a)}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-[11px] text-[var(--text-muted)] mt-1">
                      選択した住所を請求書に固定保存します（顧客の既定が後で変わっても変わりません）。
                    </p>
                  </div>
                </div>
              );
            })()}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditingInvoiceMeta(null)}
              disabled={updateInvoiceMeta.isPending}
            >
              キャンセル
            </Button>
            <Button
              variant="primary"
              disabled={updateInvoiceMeta.isPending}
              onClick={async () => {
                if (!editingInvoiceMeta) return;
                try {
                  // 請求先住所スナップショットの解決
                  let billingAddressSnapshot: BillingAddress | null | undefined;
                  const choice = editingInvoiceMeta.billingChoice;
                  if (choice === '__CURRENT__') {
                    billingAddressSnapshot = undefined; // 変更しない
                  } else if (choice === '__DEFAULT__') {
                    billingAddressSnapshot = contact ? getDefaultBillingAddress(contact) : null;
                  } else {
                    billingAddressSnapshot =
                      (contact ? getBillingAddresses(contact) : []).find((a) => a.id === choice) ??
                      undefined;
                  }
                  await updateInvoiceMeta.mutateAsync({
                    dealId: deal.id,
                    invoiceId: editingInvoiceMeta.invoiceId,
                    title: editingInvoiceMeta.title.trim() || null,
                    ...(billingAddressSnapshot !== undefined ? { billingAddressSnapshot } : {}),
                  });
                  toast.success('請求書を更新しました');
                  setEditingInvoiceMeta(null);
                } catch (err) {
                  toast.error(`失敗: ${(err as Error).message}`);
                }
              }}
            >
              {updateInvoiceMeta.isPending ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────
// 共通
// ─────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  className,
  children,
}: {
  title: string;
  icon: typeof Receipt;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 ${className ?? ''}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="size-4 text-[var(--accent)]" />
        <h3 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-1">
      {children}
    </div>
  );
}

function RowHeader({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-between gap-2 mb-1">{children}</div>;
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className="text-[var(--text-secondary)] tabular-nums">{children}</span>
    </div>
  );
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}
